import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Download, Upload, Trash2, CreditCard } from 'lucide-react';
import { api } from '../../api/client';
import { queryClient } from '../../api/queryClient';
import { useAppStore } from '../../store';
import { CalendarSettings } from './CalendarSettings';
import { AccountSettings } from './AccountSettings';
import { TimezoneSelector } from './TimezoneSelector';

interface SettingsPanelProps {
  onClose: () => void;
}


export function SettingsPanel({ onClose }: SettingsPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [restoreMsg, setRestoreMsg] = useState('');
  const theme = useAppStore((s) => s.theme);
  const setTheme = useAppStore((s) => s.setTheme);

  const handleExport = async () => {
    const data = await api.data.export();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `racetrack-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.data.import(data);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      setRestoreMsg('Restored successfully!');
      setTimeout(() => setRestoreMsg(''), 3000);
    } catch {
      setRestoreMsg('Failed to restore. Invalid file.');
    }
    e.target.value = '';
  };

  const handleManageBilling = async () => {
    const apiBase = import.meta.env.VITE_API_BASE ?? "";
    const token = useAppStore.getState().authToken;
    const res = await fetch(`${apiBase}/api/billing/create-portal`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return;
    const { url } = await res.json();
    if (url) window.location.href = url;
  };

  const handleClearData = async () => {
    if (!confirm('This will delete ALL your data. Are you sure?')) return;
    await api.data.clear();
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-base/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="surface-overlay w-[460px] p-0"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border-subtle">
          <h3 className="text-title text-text-primary">Settings</h3>
          <button onClick={onClose} aria-label="Close settings" className="text-text-muted hover:text-text-secondary p-2 rounded-lg interactive-ghost"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Appearance */}
          <div className="surface-card p-3">
            <h4 className="text-label text-text-muted mb-3">Appearance</h4>
            <div className="flex gap-2">
              <button
                onClick={() => setTheme('midnight')}
                className={`flex-1 flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-[background,box-shadow] duration-150 ${
                  theme === 'midnight'
                    ? 'ring-2 ring-accent bg-accent/8'
                    : 'hover:bg-bg-raised'
                }`}
              >
                <div className="w-8 h-8 rounded-md overflow-hidden flex flex-col shrink-0 border border-border-subtle">
                  <div className="flex-1" style={{ backgroundColor: '#07070a' }} />
                  <div className="flex-1" style={{ backgroundColor: '#131318' }} />
                  <div className="h-1" style={{ backgroundColor: '#6366f1' }} />
                </div>
                <div>
                  <span className="text-caption font-medium text-text-primary block">Dark</span>
                  <span className="text-micro text-text-muted">Midnight</span>
                </div>
              </button>
              <button
                onClick={() => setTheme('dawn')}
                className={`flex-1 flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-[background,box-shadow] duration-150 ${
                  theme === 'dawn'
                    ? 'ring-2 ring-accent bg-accent/8'
                    : 'hover:bg-bg-raised'
                }`}
              >
                <div className="w-8 h-8 rounded-md overflow-hidden flex flex-col shrink-0 border border-border-subtle">
                  <div className="flex-1" style={{ backgroundColor: '#f8f6f1' }} />
                  <div className="flex-1" style={{ backgroundColor: '#eae7df' }} />
                  <div className="h-1" style={{ backgroundColor: '#4f46e5' }} />
                </div>
                <div>
                  <span className="text-caption font-medium text-text-primary block">Light</span>
                  <span className="text-micro text-text-muted">Dawn</span>
                </div>
              </button>
            </div>
          </div>

          {/* Timezone */}
          <div className="surface-card p-3">
            <h4 className="text-label text-text-muted mb-3">Timezone</h4>
            <TimezoneSelector />
          </div>

          {/* Data */}
          <div className="surface-card p-4 space-y-2">
            <h4 className="text-label text-text-muted mb-3">Data</h4>
            <button onClick={handleExport} className="w-full flex items-center gap-3 rounded-lg px-3.5 py-3 text-body text-text-primary interactive-ghost">
              <Download size={16} className="text-accent" /> Export backup
            </button>
            <button onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-3 rounded-lg px-3.5 py-3 text-body text-text-primary interactive-ghost">
              <Upload size={16} className="text-accent" /> Import backup
            </button>
            <input ref={fileRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
            {restoreMsg && <p className={`text-caption ${restoreMsg.includes('Failed') ? 'text-p1' : 'text-finish'}`}>{restoreMsg}</p>}
          </div>

          {/* Calendars */}
          <div className="surface-card p-4 space-y-2">
            <CalendarSettings />
          </div>

          {/* Account */}
          <div className="surface-card p-4 space-y-2">
            <AccountSettings />
            {import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY && (
              <button onClick={handleManageBilling} className="w-full flex items-center gap-3 rounded-lg px-3.5 py-3 text-body text-text-primary interactive-ghost">
                <CreditCard size={16} className="text-accent" /> Manage billing
              </button>
            )}
          </div>

          {/* Danger */}
          <div className="surface-card p-4 border-p1/20">
            <h4 className="text-label text-p1/70 mb-3">Danger Zone</h4>
            <button onClick={handleClearData} className="w-full flex items-center gap-3 rounded-lg px-3.5 py-3 text-body text-p1 hover:bg-p1/10 transition-colors">
              <Trash2 size={16} /> Clear all data
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
